import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Power, PowerOff } from 'lucide-react';
import { btn, btnVoltar, sel, aviso, tabela, thEscuro, thEscuroNum, thEscuroCentro, td, tdNum, IconeAcao, useOrdenacao, COR_MODULO } from './escala-ui';

// Os três cadastros que sustentam a ferramenta: turnos, cargos e orçamento.
// São CRUDs pequenos — sem teste, conforme o PROCESSO (tela e CRUD não se testa).

const API = '/api/rh/dimensionamento-escala';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const authJson = () => ({ ...auth(), 'Content-Type': 'application/json' });

// 'HH:MM:SS' do banco → '08h', que é como a escala se lê.
const hora = (v) => `${String(v || '').slice(0, 2)}h`;

const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];
// ⚠️ A aba se chama ESCALAS (Rodrigo, 15/09). O `id` continua 'turnos' porque
//    é o que a rota e a tabela do banco usam — renomear os dois custaria
//    migration e não muda nada para quem lê a tela.
const ABAS = [['turnos', 'Escalas'], ['cargos', 'Cargos'], ['orcamento', 'Orçamento']];

const RHDimensionamentoEscalaCadastros = ({ onSair }) => {
    const [aba, setAba] = useState('turnos');
    const [turnos, setTurnos] = useState([]);
    const [cargos, setCargos] = useState([]);
    const [orcamentos, setOrcamentos] = useState([]);
    const [erro, setErro] = useState('');
    const [novoTurno, setNovoTurno] = useState({ escala: '12X36', hora_entrada: 8, hora_saida: 20 });
    const [novoOrc, setNovoOrc] = useState({ loja: 'ANV', ano: new Date().getFullYear(), qtd_orcada: '', composicao: '' });

    // ⚠️ Um hook por tabela, todos no topo: hook não se chama dentro de `if`.
    const ordTurnos = useOrdenacao(turnos, 'ESCALA');
    const ordCargos = useOrdenacao(cargos, 'ORDEM');
    const ordOrc = useOrdenacao(orcamentos, 'LOJA');

    const carregar = useCallback(async () => {
        try {
            const [rt, rc, ro] = await Promise.all([
                fetch(`${API}/turnos`, { headers: auth() }),
                fetch(`${API}/cargos`, { headers: auth() }),
                fetch(`${API}/orcamento`, { headers: auth() }),
            ]);
            if (rt.ok) setTurnos(await rt.json());
            if (rc.ok) setCargos((await rc.json()).mapa || []);
            if (ro.ok) setOrcamentos(await ro.json());
        } catch (e) { setErro('Erro ao carregar os cadastros.'); }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const enviar = async (url, metodo, corpo) => {
        const r = await fetch(url, { method: metodo, headers: authJson(), body: JSON.stringify(corpo) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setErro(d?.message || 'Não foi possível concluir.'); return false; }
        setErro(''); await carregar(); return true;
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                {ABAS.map(([id, rotulo]) => (
                    <button key={id} onClick={() => setAba(id)}
                        style={{ ...btn(aba === id ? COR_MODULO : '#fff', aba === id ? '#fff' : '#475569'), border: aba === id ? 'none' : '1px solid #e2e8f0', padding: '8px 16px', fontSize: 13 }}>
                        {rotulo}
                    </button>
                ))}
            </div>

            {erro && <div style={aviso('#fee2e2', '#991b1b')}>{erro}</div>}

            {/* ---------------- TURNOS ---------------- */}
            {aba === 'turnos' && (
                <>
                    <div style={aviso('#f1f5f9', '#475569')}>
                        O catálogo é <b>da rede</b>, sem filtro por loja. É ele que substitui o nome de escala digitado
                        à mão na planilha — lá a mesma escala aparecia com até <b>12 grafias diferentes</b>.
                        <br />⚠️ Escala em uso <b>não pode ter o horário editado</b>: isso reescreveria, em silêncio,
                        toda versão que aponta para ela. Horário novo é escala nova; a antiga se desativa.
                        <br />⚠️⚠️ <b>NOTURNA</b> é <b>derivada</b>, não marcada: é a escala com <b>6h ou mais</b> entre
                        22h e 5h, e é ela que explica a mesma escala render <b>1h a menos</b> de jornada. Escala nova já
                        nasce classificada — ninguém precisa lembrar de marcar, e ninguém pode marcar errado.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                        <select value={novoTurno.escala} onChange={(e) => setNovoTurno({ ...novoTurno, escala: e.target.value })} style={sel}>
                            <option value="12X36">12X36</option>
                            <option value="5X2">5X2</option>
                        </select>
                        <label style={{ fontSize: 12, color: '#64748b' }}>entra</label>
                        <input type="number" min={0} max={23} value={novoTurno.hora_entrada}
                            onChange={(e) => setNovoTurno({ ...novoTurno, hora_entrada: e.target.value })} style={{ ...sel, width: 64 }} />
                        <label style={{ fontSize: 12, color: '#64748b' }}>sai</label>
                        <input type="number" min={0} max={23} value={novoTurno.hora_saida}
                            onChange={(e) => setNovoTurno({ ...novoTurno, hora_saida: e.target.value })} style={{ ...sel, width: 64 }} />
                        <button onClick={() => enviar(`${API}/turnos`, 'POST', {
                            escala: novoTurno.escala,
                            hora_entrada: Number(novoTurno.hora_entrada),
                            hora_saida: Number(novoTurno.hora_saida),
                        })} style={btn(COR_MODULO, '#fff')}><Plus size={14} /> Cadastrar turno</button>
                        <span style={{ fontSize: 12, color: '#64748b' }}>Horas cheias (0 a 23). Turno que vira a meia-noite é normal.</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={tabela}>
                            <thead><tr style={{ background: '#0f172a' }}>
                                <th {...ordTurnos.cabecalho('ESCALA', thEscuro)}>Escala <ordTurnos.Seta campo="ESCALA" /></th>
                                <th {...ordTurnos.cabecalho('HORA_ENTRADA', thEscuroCentro)}>Entrada <ordTurnos.Seta campo="HORA_ENTRADA" /></th>
                                <th {...ordTurnos.cabecalho('HORA_SAIDA', thEscuroCentro)}>Saída <ordTurnos.Seta campo="HORA_SAIDA" /></th>
                                <th {...ordTurnos.cabecalho('noturno', thEscuroCentro)}>Noturna <ordTurnos.Seta campo="noturno" /></th>
                                <th {...ordTurnos.cabecalho('horas_pagas', thEscuroNum)}>Horas pagas <ordTurnos.Seta campo="horas_pagas" /></th>
                                <th style={thEscuroNum}>Efetivo ×</th>
                                <th {...ordTurnos.cabecalho('ATIVO', thEscuro)}>Status <ordTurnos.Seta campo="ATIVO" /></th>
                                <th style={thEscuro}>Ações</th>
                            </tr></thead>
                            <tbody>
                                {ordTurnos.linhas.map((t) => (
                                    <tr key={t.ID} style={{ opacity: t.ATIVO ? 1 : 0.5 }}>
                                        {/* ⚠️ Escala, entrada e saída em colunas SEPARADAS (Rodrigo,
                                            15/09) — juntas num texto só não dá para ordenar por hora
                                            nem comparar duas escalas de relance. */}
                                        <td style={{ ...td, fontWeight: 800 }}>{t.ESCALA}</td>
                                        <td style={{ ...td, textAlign: 'center' }}>{hora(t.HORA_ENTRADA)}</td>
                                        <td style={{ ...td, textAlign: 'center' }}>{hora(t.HORA_SAIDA)}</td>
                                        <td style={{ ...td, textAlign: 'center' }}
                                            title={t.noturno
                                                ? `${t.horas_noturnas}h entre 22h e 5h — jornada reduzida em 1h`
                                                : `${t.horas_noturnas || 0}h entre 22h e 5h — jornada cheia`}>
                                            {t.noturno
                                                ? <span style={{ background: '#1e293b', color: '#fff', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                                                    Sim · {t.horas_noturnas}h</span>
                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                        </td>
                                        <td style={tdNum}>{t.horas_pagas}h</td>
                                        <td style={tdNum} title="No 12x36 a mesma posição exige duas pessoas (turma A e turma B).">
                                            {t.ESCALA === '12X36' ? '2' : '1'}
                                        </td>
                                        <td style={td}>{t.ATIVO ? 'ativo' : 'desativado'}</td>
                                        <td style={td}>
                                            <IconeAcao onClick={() => enviar(`${API}/turnos/${t.ID}`, 'PUT', { ativo: !t.ATIVO })}
                                                cor={t.ATIVO ? '#e11d48' : '#16a34a'}
                                                titulo={t.ATIVO
                                                    ? 'Desativar — some do seletor da grade; versões que já o usam continuam válidas'
                                                    : 'Reativar — volta a aparecer no seletor da grade'}>
                                                {t.ATIVO ? <PowerOff size={17} /> : <Power size={17} />}
                                            </IconeAcao>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ---------------- CARGOS ---------------- */}
            {aba === 'cargos' && (
                <>
                    <div style={aviso('#f1f5f9', '#475569')}>
                        O vocabulário vem da <b>`dtbCargos.NOME_CARGO_ABREV`</b> (grupo Operação / Crew), que já é o
                        mesmo da planilha. Aqui mora só a equivalência <b>desta ferramenta</b>.
                        <br />⚠️ <b>TREINADOR aponta para ATENDENTE LANCHONETE</b>: os dois são iguais <b>no
                        posicionamento da escala</b>, e só nisso — a `dtbCargos` continua tratando os dois
                        separadamente, que é o que o RH precisa nas outras medições.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={tabela}>
                            <thead><tr style={{ background: '#0f172a' }}>
                                <th {...ordCargos.cabecalho('CARGO_ABREV', thEscuro)}>Cargo na folha (abreviado) <ordCargos.Seta campo="CARGO_ABREV" /></th>
                                <th {...ordCargos.cabecalho('CARGO_ESCALA', thEscuro)}>Entra na escala como <ordCargos.Seta campo="CARGO_ESCALA" /></th>
                                <th {...ordCargos.cabecalho('ORDEM', thEscuroNum)}>Ordem na grade <ordCargos.Seta campo="ORDEM" /></th>
                                <th {...ordCargos.cabecalho('ATIVO', thEscuro)}>Status <ordCargos.Seta campo="ATIVO" /></th>
                                <th style={thEscuro}>Ações</th>
                            </tr></thead>
                            <tbody>
                                {ordCargos.linhas.map((c) => (
                                    <tr key={c.CARGO_ABREV} style={{ opacity: c.ATIVO ? 1 : 0.5 }}>
                                        <td style={{ ...td, fontWeight: 700 }}>{c.CARGO_ABREV}</td>
                                        <td style={td}>
                                            {c.CARGO_ESCALA}
                                            {c.CARGO_ABREV !== c.CARGO_ESCALA && (
                                                <span style={{ color: '#b45309', fontSize: 11 }}> · agrupado</span>
                                            )}
                                        </td>
                                        <td style={tdNum} title="Governa a ordem das linhas na grade e a paleta de cores">{c.ORDEM}</td>
                                        <td style={td}>
                                            {c.ATIVO ? 'na grade' : 'fora da grade'}
                                            {c.CARGO_ABREV === 'MENOR APRENDIZ' && !c.ATIVO && (
                                                <div style={{ fontSize: 11, color: '#64748b' }}>fase 2 — o estudo nunca dimensionou o aprendiz</div>
                                            )}
                                        </td>
                                        <td style={td}>
                                            <IconeAcao onClick={() => enviar(`${API}/cargos/${encodeURIComponent(c.CARGO_ABREV)}`, 'PUT', { ativo: !c.ATIVO })}
                                                cor={c.ATIVO ? '#e11d48' : '#16a34a'}
                                                titulo={c.ATIVO ? 'Tirar da grade — deixa de ser oferecido no seletor de cargo'
                                                    : 'Incluir na grade — passa a ser dimensionável'}>
                                                {c.ATIVO ? <PowerOff size={17} /> : <Power size={17} />}
                                            </IconeAcao>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ---------------- ORÇAMENTO ---------------- */}
            {aba === 'orcamento' && (
                <>
                    <div style={aviso('#fef3c7', '#92400e')}>
                        ⚠️ <b>Esta tabela nasceu vazia de propósito.</b> Os números de 2024/2025 que existiam na aba
                        ORÇAMENTO da planilha <b>não foram carregados</b> — decisão do Rodrigo em 14/09, por não
                        considerá-los confiáveis hoje. Cadastre aqui quando houver número em que se possa confiar.
                        <br />Sem orçamento cadastrado, a coluna <b>× Orçado</b> aparece como “—” e nada quebra.
                        <br />⚠️ O que se compara ao orçado é o <b>EFETIVO</b> (12x36 contado em dobro), nunca as posições.
                        A <b>composição</b> é descritiva e não é validada — loja de shopping não tem ASG nem porteiro.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                        <select value={novoOrc.loja} onChange={(e) => setNovoOrc({ ...novoOrc, loja: e.target.value })} style={sel}>
                            {LOJAS.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <input type="number" value={novoOrc.ano} onChange={(e) => setNovoOrc({ ...novoOrc, ano: e.target.value })}
                            style={{ ...sel, width: 88 }} title="Ano" />
                        <input type="number" min={0} placeholder="Quadro" value={novoOrc.qtd_orcada}
                            onChange={(e) => setNovoOrc({ ...novoOrc, qtd_orcada: e.target.value })} style={{ ...sel, width: 90 }} />
                        <input type="text" placeholder="Composição (ex.: AT+EST+ASG+ANF)" value={novoOrc.composicao}
                            onChange={(e) => setNovoOrc({ ...novoOrc, composicao: e.target.value })} style={{ ...sel, width: 240 }} />
                        <button onClick={async () => {
                            const feito = await enviar(`${API}/orcamento`, 'POST', {
                                loja: novoOrc.loja, ano: Number(novoOrc.ano),
                                qtd_orcada: Number(novoOrc.qtd_orcada), composicao: novoOrc.composicao || null,
                            });
                            if (feito) setNovoOrc({ ...novoOrc, qtd_orcada: '', composicao: '' });
                        }} style={btn(COR_MODULO, '#fff')}><Plus size={14} /> Gravar</button>
                    </div>
                    {orcamentos.length === 0 ? (
                        <div style={aviso('#f1f5f9', '#475569')}>Nenhum orçamento cadastrado ainda.</div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tabela}>
                                <thead><tr style={{ background: '#0f172a' }}>
                                    <th {...ordOrc.cabecalho('LOJA', thEscuro)}>Loja <ordOrc.Seta campo="LOJA" /></th>
                                    <th {...ordOrc.cabecalho('ANO', thEscuroNum)}>Ano <ordOrc.Seta campo="ANO" /></th>
                                    <th {...ordOrc.cabecalho('QTD_ORCADA', thEscuroNum)}>Quadro orçado <ordOrc.Seta campo="QTD_ORCADA" /></th>
                                    <th {...ordOrc.cabecalho('COMPOSICAO', thEscuro)}>Composição <ordOrc.Seta campo="COMPOSICAO" /></th>
                                </tr></thead>
                                <tbody>
                                    {ordOrc.linhas.map((o) => (
                                        <tr key={o.ID}>
                                            <td style={{ ...td, fontWeight: 700 }}>{o.LOJA}</td>
                                            <td style={tdNum}>{o.ANO}</td>
                                            <td style={tdNum}>{o.QTD_ORCADA}</td>
                                            <td style={td}>{o.COMPOSICAO || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RHDimensionamentoEscalaCadastros;
